import { computed, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RouteResponse, RouteRequestPayload } from '../models/route-selection.model';
import { RouteReplayDataService } from './route-replay-data.service';
import { RouteReplayResponse } from '../models/route-replay-response.model';
import { ColorService } from './color.service';
import * as dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class RouteReplayService {
  dataService=inject(RouteReplayDataService);
  buid=computed(()=>this.dataService.buid());
  private colorService = inject(ColorService);

  constructor(private http: HttpClient) { }

  getRouteDetails(payload: RouteRequestPayload): Observable<RouteResponse[]> {
    return this.http.post<RouteResponse[]>(
      `${environment.troubleShootingApiUrl}/troubleshooting/api/routes/${this.buid()}/routes`,
      payload
    );
  }

  // Unified bulk API method - handles both single and multiple routes
  getBulkRouteReplayData(routeIds: string[]): Observable<any> {
    // Reset color service for new route data
    this.colorService.reset();
    
    // POST request with array of route IDs
    return this.http.post<any>(
      `${environment.troubleShootingApiUrl}/troubleshooting/api/routes/${this.buid()}/routes/bulk`,
      routeIds
    )
    // return of( AJAY_DUMMY_RES)
    .pipe(
      tap((res) => {
        // Always use multi-route flow (even for single route)
        const transformedRoutes: any[] = [];
        const routeKeys = Object.keys(res);
        const isSingleRoute = routeKeys.length === 1;
        
        routeKeys.forEach((routeId, index) => {
          const routeData = res[routeId];
          
          if (routeData && routeData.vehicles) {
            routeData.vehicles = routeData.vehicles.map((vehicle: any) => {
              if (vehicle.milestonesData && Array.isArray(vehicle.milestonesData)) {
                const locationList = vehicle.milestonesData
                  .map((milestone: any) => {
                    if (!milestone.locationCO || !milestone.locationCO.geocords) {
                      return null;
                    }
                    
                    const geocords = milestone.locationCO.geocords.split(',');
                    if (geocords.length !== 2) {
                      return null;
                    }
                    
                    return {
                      lat: parseFloat(geocords[0]),
                      lng: parseFloat(geocords[1]),
                      time: milestone.locationCO.timestamp || 0,
                      speed: (milestone.locationCO.speed || 0) * 3.6 
                    };
                  })
                  .filter((location: any) => location !== null);
                
                vehicle.milestonesData = {
                  locationList: locationList
                };
              } else {
                vehicle.milestonesData = {
                  locationList: []
                };
              }
              
              vehicle.color = this.colorService.getUniqueColor(vehicle.etsVehicle.guId);
              vehicle.selected = false;
              
              if (vehicle.startDateTime) {
                vehicle.startDateTimeEpoch = dayjs(vehicle.startDateTime, 'DD-MM-YYYY HH:mm:ss').valueOf();
              }
              if (vehicle.endDateTime) {
                vehicle.endDateTimeEpoch = dayjs(vehicle.endDateTime, 'DD-MM-YYYY HH:mm:ss').valueOf();
              }
              
              return vehicle;
            }).sort((a: any, b: any) => {
              const epochA = a.startDateTimeEpoch || 0;
              const epochB = b.startDateTimeEpoch || 0;
              return epochA - epochB;
            });
          }
          
          transformedRoutes.push({
            ...routeData,
            routeId: routeId,
            isExpanded: isSingleRoute, // Expand if only one route, collapse if multiple
            index: index + 1
          });
        });
        
        // Always update multi-route data (works for single route too)
        this.dataService.updateMultiRouteData(transformedRoutes);
      })
    );
  }
}
